import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Avatar,
  Stack,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore,
  MonitorHeart,
  HealthAndSafety,
  Timeline,
  Security,
  Group,
  Assessment,
  Analytics,
  Warning,
  CheckCircle,
  Info,
  LocalHospital,
  Favorite,
  Shield,
  Cloud,
  DeviceHub,
  School,
  Support,
  Phone,
  Email,
  Launch
} from '@mui/icons-material';

const AboutPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedFAQ, setExpandedFAQ] = useState(false);

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const aboutSections = [
    {
      icon: <MonitorHeart />,
      title: "What is CheckYourMate?",
      content: "CheckYourMate is a comprehensive digital health platform designed to help you monitor, track, and manage your blood pressure and hypertension. Our platform combines advanced analytics with user-friendly tools to provide personalized insights for better cardiovascular health management."
    },
    {
      icon: <HealthAndSafety />,
      title: "Understanding Hypertension",
      content: "Hypertension, commonly known as high blood pressure, is often called the 'silent killer' because it typically has no symptoms but can lead to serious health complications if left untreated. Our platform helps you track and manage this condition effectively."
    },
    {
      icon: <Timeline />,
      title: "How We Help",
      content: "We provide intelligent tracking, trend analysis, and personalized recommendations to help you maintain healthy blood pressure levels. Our tools enable you to work effectively with healthcare providers to prevent and manage hypertension."
    },
    {
      icon: <Security />,
      title: "Privacy & Security",
      content: "Your health data is protected with enterprise-grade encryption and security measures. We comply with healthcare data protection regulations and never share your personal information without your explicit consent."
    }
  ];

  const features = [
    {
      icon: <Analytics />,
      title: "Advanced Analytics",
      description: "AI-powered insights to identify patterns and trends in your blood pressure data"
    },
    {
      icon: <DeviceHub />,
      title: "Device Integration",
      description: "Connect with popular blood pressure monitors for seamless data collection"
    },
    {
      icon: <Assessment />,
      title: "Healthcare Reports",
      description: "Generate comprehensive reports for your healthcare providers"
    },
    {
      icon: <Group />,
      title: "Care Team Sharing",
      description: "Safely share your data with family members and healthcare providers"
    },
    {
      icon: <Cloud />,
      title: "Cloud Sync",
      description: "Access your data anywhere with secure cloud synchronization"
    },
    {
      icon: <Shield />,
      title: "Data Protection",
      description: "Enterprise-grade security to protect your sensitive health information"
    }
  ];

  const hypertensionInfo = [
    {
      category: "Normal",
      systolic: "Less than 120",
      diastolic: "Less than 80",
      description: "Maintain healthy lifestyle choices",
      color: "success"
    },
    {
      category: "Elevated",
      systolic: "120-129",
      diastolic: "Less than 80",
      description: "Lifestyle changes recommended",
      color: "warning"
    },
    {
      category: "Stage 1 Hypertension",
      systolic: "130-139",
      diastolic: "80-89",
      description: "Medical evaluation and lifestyle changes",
      color: "error"
    },
    {
      category: "Stage 2 Hypertension",
      systolic: "140/90",
      diastolic: "or higher",
      description: "Immediate medical attention required",
      color: "error"
    }
  ];

  const faqData = [
    {
      question: "What is hypertension and why is it dangerous?",
      answer: "Hypertension is when your blood pressure is consistently 130/80 mmHg or higher. It's called the 'silent killer' because it often has no symptoms but can lead to heart disease, stroke, kidney disease, and other serious complications if left untreated. Regular monitoring is crucial for early detection and management."
    },
    {
      question: "How often should I check my blood pressure?",
      answer: "For most adults, checking blood pressure at least once every two years is recommended if it's normal. If you have hypertension or are at risk, your doctor may recommend daily or weekly monitoring. Our platform helps you track readings and set reminders for consistent monitoring."
    },
    {
      question: "Is my health data secure on CheckYourMate?",
      answer: "Yes, we use enterprise-grade encryption and security measures to protect your data. We comply with healthcare data protection regulations including HIPAA standards. Your data is never shared with third parties without your explicit consent, and you maintain full control over who can access your information."
    },
    {
      question: "Can I share my data with my doctor?",
      answer: "Absolutely! One of our key features is the ability to generate comprehensive reports and share your blood pressure data with healthcare providers. You can export data in various formats and control exactly what information is shared and with whom."
    },
    {
      question: "What devices can I connect to CheckYourMate?",
      answer: "We support integration with most popular blood pressure monitors including Omron, Withings, and other FDA-approved devices. You can also manually enter readings from any blood pressure monitor. Our platform is designed to work with both automated and manual data entry."
    },
    {
      question: "How does the trend analysis work?",
      answer: "Our advanced analytics engine analyzes your blood pressure readings over time to identify patterns, trends, and potential triggers. It looks for correlations with factors like time of day, stress levels, medication timing, and lifestyle factors to provide personalized insights for better hypertension management."
    },
    {
      question: "Is CheckYourMate free to use?",
      answer: "Yes, CheckYourMate is completely free to use. We believe everyone should have access to tools that help them manage their cardiovascular health. All core features including tracking, analytics, and reporting are available at no cost."
    },
    {
      question: "Can family members access my data?",
      answer: "Only if you choose to share it. You have complete control over your data privacy. You can invite family members or caregivers to view your data, and you can revoke access at any time. This feature is particularly helpful for elderly users or those who want family support in managing their health."
    },
    {
      question: "What should I do if I get a high reading?",
      answer: "If you get a single high reading, don't panic. Take a few minutes to relax and take another reading. If you consistently get high readings (130/80 or higher), contact your healthcare provider. Our platform can help you track patterns and generate reports to share with your doctor."
    },
    {
      question: "Does CheckYourMate replace medical advice?",
      answer: "No, CheckYourMate is a tracking and monitoring tool, not a replacement for professional medical advice. Always consult with your healthcare provider for medical decisions. Our platform is designed to support and enhance your healthcare relationship by providing detailed tracking and insights."
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'primary.main' }}>
          <Favorite sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          About CheckYourMate
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
          Your comprehensive platform for blood pressure monitoring and hypertension management
        </Typography>
      </Box>

      {/* Mission Statement */}
      <Paper elevation={3} sx={{ p: 4, mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Our Mission
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
          To empower individuals to take control of their cardiovascular health through intelligent monitoring, 
          comprehensive tracking, and personalized insights for hypertension prevention and management.
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          We believe that everyone deserves access to tools that help them understand and manage their health. 
          CheckYourMate combines cutting-edge technology with user-friendly design to make blood pressure monitoring 
          and hypertension management accessible, effective, and empowering.
        </Typography>
      </Paper>

      {/* About Sections */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {aboutSections.map((section, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    {section.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {section.title}
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {section.content}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Features Overview */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
          Platform Features
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center', 
                  p: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 2 }}>
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Hypertension Information */}
      <Paper elevation={2} sx={{ p: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Understanding Blood Pressure Levels
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> These are general guidelines. Always consult with your healthcare provider 
            for personalized medical advice and treatment recommendations.
          </Typography>
        </Alert>
        <Grid container spacing={2}>
          {hypertensionInfo.map((info, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={info.category} 
                      color={info.color} 
                      sx={{ mr: 2, fontWeight: 'bold' }}
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {info.systolic} / {info.diastolic} mmHg
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {info.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* FAQ Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
          Frequently Asked Questions
        </Typography>
        {faqData.map((faq, index) => (
          <Accordion
            key={index}
            expanded={expandedFAQ === `panel${index}`}
            onChange={handleFAQChange(`panel${index}`)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Support Section */}
      <Paper elevation={2} sx={{ p: 4, mb: 6, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Need Help?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          We're here to support you on your health journey. Get in touch with our team.
        </Typography>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<Support />}
            size="large"
            onClick={() => navigate('/support')}
            sx={{ textTransform: 'none' }}
          >
            Support Center
          </Button>
          <Button
            variant="outlined"
            startIcon={<Email />}
            size="large"
            href="mailto:support@checkyourmate.co.uk"
            sx={{ textTransform: 'none' }}
          >
            Email Support
          </Button>
          <Button
            variant="contained"
            startIcon={<MonitorHeart />}
            size="large"
            onClick={() => navigate('/register')}
            sx={{ textTransform: 'none' }}
          >
            Get Started
          </Button>
        </Stack>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          CheckYourMate is a health tracking platform and does not provide medical advice. 
          Always consult with qualified healthcare professionals for medical decisions.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          © 2024 CheckYourMate. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default AboutPage; 